import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { clearCustomVrm, loadCustomVrm, saveCustomVrm } from '../lib/vrmStore';

/**
 * 選択中の VRM が未設定のときに使うサンプル VRM の URL。
 * リポジトリ同梱（public/samples/）なので同一オリジン配信で確実（CDN不安定を回避）。
 * base 配下に配信されるため import.meta.env.BASE_URL を前置する。
 */
const SAMPLE_VRM_URL = `${import.meta.env.BASE_URL}samples/AvatarSample.vrm`;
const SAMPLE_VRM_LABEL = 'サンプル VRM';

interface VrmContextValue {
  /** 現在選択中の VRM の URL（カスタム VRM は objectURL、未選択時はサンプルの静的 URL）。 */
  vrmUrl: string;
  /** 表示用ラベル（カスタム VRM ならファイル名、未選択なら「サンプル VRM」）。 */
  vrmLabel: string;
  /** ユーザーが選んだカスタム VRM を使用中かどうか。 */
  isCustom: boolean;
  /** IndexedDB からの初期読み込みが完了したか。 */
  ready: boolean;
  /** ファイルを選んでカスタム VRM として保存・適用する。アプリ全体に反映される。 */
  setCustomVrm: (file: File) => Promise<void>;
  /** サンプル VRM に戻す（保存済みのカスタム VRM は削除される）。 */
  resetToSample: () => Promise<void>;
}

const VrmContext = createContext<VrmContextValue | undefined>(undefined);

export function VrmProvider({ children }: { children: React.ReactNode }) {
  const [vrmUrl, setVrmUrl] = useState(SAMPLE_VRM_URL);
  const [vrmLabel, setVrmLabel] = useState(SAMPLE_VRM_LABEL);
  const [isCustom, setIsCustom] = useState(false);
  const [ready, setReady] = useState(false);

  // 直近に発行した objectURL。差し替え・アンマウント時に revoke するために保持する。
  const objectUrlRef = useRef<string | null>(null);

  // マウント時に一度だけ、保存済みのカスタム VRM を IndexedDB から読み込む。
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const record = await loadCustomVrm();
        if (cancelled) return;
        if (record) {
          const url = URL.createObjectURL(record.blob);
          objectUrlRef.current = url;
          setVrmUrl(url);
          setVrmLabel(record.name);
          setIsCustom(true);
        }
      } catch (e) {
        console.error('保存済みのカスタム VRM の読み込みに失敗しました', e);
        // 読み込みに失敗してもサンプル VRM で継続する。
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // アンマウント時に、その時点の最新 objectURL を revoke する。
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  async function setCustomVrm(file: File): Promise<void> {
    await saveCustomVrm(file, file.name);
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    setVrmUrl(url);
    setVrmLabel(file.name);
    setIsCustom(true);
  }

  async function resetToSample(): Promise<void> {
    await clearCustomVrm();
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setVrmUrl(SAMPLE_VRM_URL);
    setVrmLabel(SAMPLE_VRM_LABEL);
    setIsCustom(false);
  }

  const value: VrmContextValue = { vrmUrl, vrmLabel, isCustom, ready, setCustomVrm, resetToSample };

  return <VrmContext.Provider value={value}>{children}</VrmContext.Provider>;
}

export function useVrm(): VrmContextValue {
  const ctx = useContext(VrmContext);
  if (!ctx) throw new Error('useVrm() は VrmProvider の内側でのみ使用できます。');
  return ctx;
}
